import React from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    message,
    Space,
    Tag,
    Card,
    Tooltip,
    Empty,
    Popconfirm,
} from 'antd';
import {
    UserOutlined,
    LockOutlined,
    PhoneOutlined,
    MailOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    SafetyCertificateOutlined,
    UserDeleteOutlined,
} from '@ant-design/icons';
import {
    listUsers,
    createUser,
    updateUser,
    deleteUser,
    getUserById,
    grantAdmin,
    revokeAdmin,
} from '../../api/sys_user';
import { getUser, isAuthenticated } from '../../utils/auth';
import { formatDateTime } from '../../utils/format';
import {
    ROLES,
    getRoleLabel,
    displayRoleName,
    ROLE_TAG_COLOR,
    canCreateUser,
    canChooseRoleOnCreate,
    canGrantAdmin,
    canEditUser,
    canDeleteUser,
    canChangeStatus,
} from '../../utils/role';
import { modalWidth } from '../../utils/responsive';
import { loginIdFormValidator, LOGIN_ID_RULE_HINT } from '../../utils/loginIdPolicy';
import {
    passwordFormValidator,
    passwordRequiredValidator,
    PASSWORD_RULE_HINT,
} from '../../utils/passwordPolicy';
import './UserManager.css';

const { Option } = Select;

const PHONE_RULE = {
    pattern: /^$|^1[3-9]\d{9}$/,
    message: '请输入有效的 11 位手机号，或留空',
};

class UserManager extends React.Component {
    formRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            allUsers: [],
            users: [],
            loading: false,
            modalVisible: false,
            modalType: 'add',
            selectedUser: null,
            submitLoading: false,
            detailLoading: false,
            keyword: '',
            total: 0,
            current: 1,
            pageSize: 10,
        };
    }

    getCurrentUser = () => getUser();

    componentDidMount() {
        this.fetchUsers();
    }

    buildPageState = (allUsers, overrides = {}) => {
        const keyword = overrides.keyword !== undefined ? overrides.keyword : this.state.keyword;
        const current = overrides.current !== undefined ? overrides.current : this.state.current;
        const pageSize = overrides.pageSize !== undefined ? overrides.pageSize : this.state.pageSize;
        const kw = (keyword || '').trim().toLowerCase();
        const filtered = kw
            ? allUsers.filter(
                  (u) =>
                      (u.loginId && u.loginId.toLowerCase().includes(kw)) ||
                      (u.email && u.email.toLowerCase().includes(kw)) ||
                      (u.phone && u.phone.includes(kw)) ||
                      (u.roleKey && getRoleLabel(u.roleKey).includes(kw))
              )
            : allUsers;
        const maxPage = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
        const safeCurrent = Math.min(current, maxPage);
        const safeStart = (safeCurrent - 1) * pageSize;
        return {
            allUsers,
            keyword,
            current: safeCurrent,
            pageSize,
            users: filtered.slice(safeStart, safeStart + pageSize),
            total: filtered.length,
        };
    };

    fetchUsers = async () => {
        if (!isAuthenticated()) {
            message.warning('未检测到登录凭证，请重新登录');
            return;
        }
        this.setState({ loading: true });
        try {
            const allUsers = await listUsers();
            this.setState(this.buildPageState(allUsers, { current: 1 }));
        } catch (error) {
            message.error(error.message || '加载用户列表失败');
            this.setState({ users: [], allUsers: [], total: 0 });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSearch = (value) => {
        this.setState((prev) =>
            this.buildPageState(prev.allUsers, { keyword: value, current: 1 })
        );
    };

    handleTableChange = (pagination) => {
        this.setState((prev) =>
            this.buildPageState(prev.allUsers, {
                current: pagination.current,
                pageSize: pagination.pageSize,
            })
        );
    };

    handleAdd = async () => {
        const currentUser = this.getCurrentUser();
        try {
            const values = await this.formRef.current.validateFields();
            this.setState({ submitLoading: true });
            const payload = {
                loginId: values.loginId,
                password: values.password,
                email: values.email || undefined,
                phone: values.phone || undefined,
            };
            if (canChooseRoleOnCreate(currentUser) && values.roleKey) {
                payload.roleKey = values.roleKey;
            }
            await createUser(payload);
            message.success('添加用户成功');
            this.closeModal();
            await this.fetchUsers();
        } catch (error) {
            if (error.errorFields) {
                message.error('请填写必填项');
            } else {
                message.error(error.message || '添加用户失败');
            }
        } finally {
            this.setState({ submitLoading: false });
        }
    };

    handleEdit = async () => {
        const { selectedUser } = this.state;
        const currentUser = this.getCurrentUser();
        if (!selectedUser) {
            message.error('未找到要编辑的用户');
            return;
        }
        try {
            const values = await this.formRef.current.validateFields();
            const userData = {
                email: values.email ?? '',
                phone: values.phone ?? '',
            };
            if (canChangeStatus(currentUser, selectedUser)) {
                userData.status = values.status;
            }
            if (values.password && values.password.trim() !== '') {
                userData.password = values.password;
            }

            this.setState({ submitLoading: true });
            await updateUser(selectedUser.id, userData);
            message.success('更新用户成功');
            this.closeModal();
            await this.fetchUsers();
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写');
            } else {
                message.error(error.message || '更新用户失败');
            }
        } finally {
            this.setState({ submitLoading: false });
        }
    };

    handleDelete = (record) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除用户「${record.loginId}」吗？将执行软删除（停用）。`,
            okText: '确认删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                await deleteUser(record.id);
                message.success('删除成功');
                const { users, current } = this.state;
                if (users.length === 1 && current > 1) {
                    this.setState({ current: current - 1 }, () => this.fetchUsers());
                } else {
                    await this.fetchUsers();
                }
            },
        });
    };

    handleGrantAdmin = async (record) => {
        try {
            await grantAdmin(record.id);
            message.success(`已授权「${record.loginId}」为管理员，对方需重新登录后生效`);
            await this.fetchUsers();
        } catch (error) {
            message.error(error.message || '授权失败');
        }
    };

    handleRevokeAdmin = async (record) => {
        try {
            await revokeAdmin(record.id);
            message.success(`已撤销「${record.loginId}」的管理员权限，对方需重新登录后生效`);
            await this.fetchUsers();
        } catch (error) {
            message.error(error.message || '撤销失败');
        }
    };

    showAddModal = () => {
        const currentUser = this.getCurrentUser();
        this.setState({
            modalVisible: true,
            modalType: 'add',
            selectedUser: null,
        });
        setTimeout(() => {
            this.formRef.current?.resetFields();
            this.formRef.current?.setFieldsValue({
                roleKey: ROLES.USER,
                status: '0',
            });
            if (!canChooseRoleOnCreate(currentUser)) {
                this.formRef.current?.setFieldsValue({ roleKey: ROLES.USER });
            }
        }, 0);
    };

    showEditModal = async (userId) => {
        if (!userId) {
            message.error('用户 ID 无效');
            return;
        }
        this.setState({ detailLoading: true, modalVisible: true, modalType: 'edit' });
        try {
            const userInfo = await getUserById(userId);
            if (!userInfo) {
                message.error('用户不存在');
                this.setState({ modalVisible: false, detailLoading: false });
                return;
            }
            this.setState({ selectedUser: userInfo, detailLoading: false });
            setTimeout(() => {
                this.formRef.current?.setFieldsValue({
                    loginId: userInfo.loginId || '',
                    email: userInfo.email || '',
                    phone: userInfo.phone || '',
                    status: userInfo.status || '0',
                    password: '',
                });
            }, 0);
        } catch (error) {
            message.error(error.message || '获取用户详情失败');
            this.setState({ modalVisible: false, detailLoading: false });
        }
    };

    closeModal = () => {
        this.setState({ modalVisible: false, selectedUser: null });
        this.formRef.current?.resetFields();
    };

    renderActions = (_, record) => {
        const currentUser = this.getCurrentUser();
        const showEdit = canEditUser(currentUser, record);
        const showDelete = canDeleteUser(currentUser, record);
        const showGrant = canGrantAdmin(currentUser) && record.roleKey === ROLES.USER;
        const showRevoke = canGrantAdmin(currentUser) && record.roleKey === ROLES.ADMIN;

        return (
            <Space size="small" wrap>
                {showGrant && (
                    <Popconfirm
                        title={`确认将「${record.loginId}」授权为管理员？`}
                        onConfirm={() => this.handleGrantAdmin(record)}
                    >
                        <Button type="link" size="small" icon={<SafetyCertificateOutlined />}>
                            授权管理员
                        </Button>
                    </Popconfirm>
                )}
                {showRevoke && (
                    <Popconfirm
                        title={`确认撤销「${record.loginId}」的管理员权限？`}
                        onConfirm={() => this.handleRevokeAdmin(record)}
                    >
                        <Button type="link" size="small" danger icon={<UserDeleteOutlined />}>
                            撤销管理员
                        </Button>
                    </Popconfirm>
                )}
                <Tooltip title={showEdit ? '编辑' : '无权限'}>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        disabled={!showEdit}
                        onClick={() => this.showEditModal(record.id)}
                    />
                </Tooltip>
                {showDelete && (
                    <Tooltip title="软删除">
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => this.handleDelete(record)}
                        />
                    </Tooltip>
                )}
            </Space>
        );
    };

    getColumns = () => [
        { title: 'ID', dataIndex: 'id', width: 72 },
        {
            title: '角色',
            dataIndex: 'roleKey',
            width: 120,
            render: (roleKey, record) => (
                <Tag color={ROLE_TAG_COLOR[roleKey] || 'default'}>
                    {displayRoleName(record)}
                </Tag>
            ),
        },
        { title: '登录名', dataIndex: 'loginId', ellipsis: true },
        { title: '邮箱', dataIndex: 'email', ellipsis: true, render: (v) => v || '—' },
        { title: '手机', dataIndex: 'phone', width: 130, render: (v) => v || '—' },
        {
            title: '状态',
            dataIndex: 'status',
            width: 88,
            render: (status) => (
                <Tag color={status === '0' ? 'success' : 'error'}>
                    {status === '0' ? '正常' : '停用'}
                </Tag>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            width: 168,
            render: formatDateTime,
        },
        {
            title: '操作',
            key: 'action',
            width: 280,
            fixed: 'right',
            render: this.renderActions,
        },
    ];

    render() {
        const currentUser = this.getCurrentUser();
        const {
            users,
            loading,
            modalVisible,
            modalType,
            current,
            pageSize,
            total,
            submitLoading,
            detailLoading,
            allUsers,
            selectedUser,
        } = this.state;
        const isAdd = modalType === 'add';
        const statusDisabled =
            !isAdd && selectedUser && !canChangeStatus(currentUser, selectedUser);

        return (
            <div className="user-manager">
                <Card className="user-manager__card" bordered={false}>
                    <div className="user-manager__header">
                        <h2 className="user-manager__title">用户账号管理</h2>
                        <Space wrap>
                            <Input.Search
                                placeholder="搜索登录名 / 邮箱 / 手机 / 角色"
                                allowClear
                                enterButton={<SearchOutlined />}
                                onSearch={this.handleSearch}
                                onChange={(e) => {
                                    if (!e.target.value) this.handleSearch('');
                                }}
                                style={{ width: 300 }}
                            />
                            <Button icon={<ReloadOutlined />} onClick={this.fetchUsers}>
                                刷新
                            </Button>
                            {canCreateUser(currentUser) && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={this.showAddModal}
                                >
                                    添加用户
                                </Button>
                            )}
                        </Space>
                    </div>

                    <Table
                        className="user-manager__table"
                        columns={this.getColumns()}
                        dataSource={users}
                        loading={loading}
                        rowKey="id"
                        scroll={{ x: 1100 }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        allUsers.length === 0 && !loading
                                            ? '暂无用户数据'
                                            : '无匹配结果'
                                    }
                                />
                            ),
                        }}
                        pagination={{
                            current,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (t) => `共 ${t} 条`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={this.handleTableChange}
                    />
                </Card>

                <Modal
                    title={isAdd ? '添加用户' : '编辑用户'}
                    open={modalVisible}
                    onOk={isAdd ? this.handleAdd : this.handleEdit}
                    onCancel={this.closeModal}
                    confirmLoading={submitLoading}
                    width={modalWidth(520)}
                    destroyOnClose
                    okText={isAdd ? '创建' : '保存'}
                    cancelText="取消"
                >
                    {detailLoading ? (
                        <div className="user-manager__modal-loading">加载中...</div>
                    ) : (
                        <Form ref={this.formRef} layout="vertical" initialValues={{ status: '0', roleKey: ROLES.USER }}>
                            <Form.Item
                                name="loginId"
                                label="登录名"
                                extra={LOGIN_ID_RULE_HINT}
                                rules={[{ validator: loginIdFormValidator() }]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    disabled={!isAdd}
                                    placeholder="登录名"
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label={isAdd ? '密码' : '新密码'}
                                extra={PASSWORD_RULE_HINT}
                                rules={[
                                    isAdd
                                        ? {
                                              validator: passwordRequiredValidator(() =>
                                                  this.formRef.current?.getFieldValue('loginId')
                                              ),
                                          }
                                        : {
                                              validator: passwordFormValidator(() =>
                                                  this.state.selectedUser?.loginId ||
                                                  this.formRef.current?.getFieldValue('loginId')
                                              ),
                                          },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder={isAdd ? '必填' : '不修改请留空'}
                                />
                            </Form.Item>
                            {isAdd && canChooseRoleOnCreate(currentUser) && (
                                <Form.Item
                                    name="roleKey"
                                    label="角色"
                                    rules={[{ required: true, message: '请选择角色' }]}
                                >
                                    <Select placeholder="选择角色">
                                        <Option value={ROLES.USER}>{getRoleLabel(ROLES.USER)}</Option>
                                        <Option value={ROLES.ADMIN}>{getRoleLabel(ROLES.ADMIN)}</Option>
                                    </Select>
                                </Form.Item>
                            )}
                            <Form.Item
                                name="email"
                                label="邮箱"
                                rules={[{ type: 'email', message: '邮箱格式不正确' }]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="选填" allowClear />
                            </Form.Item>
                            <Form.Item name="phone" label="手机" rules={[PHONE_RULE]}>
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="选填"
                                    maxLength={11}
                                    allowClear
                                />
                            </Form.Item>
                            {!isAdd && (
                                <Form.Item
                                    name="status"
                                    label="状态"
                                    rules={[{ required: true, message: '请选择状态' }]}
                                >
                                    <Select disabled={statusDisabled}>
                                        <Option value="0">正常</Option>
                                        <Option value="1">停用</Option>
                                    </Select>
                                </Form.Item>
                            )}
                        </Form>
                    )}
                </Modal>
            </div>
        );
    }
}

export default UserManager;
